import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Building2, Edit } from 'lucide-react';
import { useData } from '../../context/DataContext';

export default function PrincipalStaffDirectory() {
    const { departments } = useData();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL'); // ALL, HOD, FACULTY
    const [searchTerm, setSearchTerm] = useState('');

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [targetDept, setTargetDept] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [usersRes, facultyRes] = await Promise.all([
                fetch('/api/users'),
                fetch('/api/faculty')
            ]);

            const users = await usersRes.json();
            const facultyList = await facultyRes.json();

            // Merge User data with Faculty data (to get Dept ID)
            const staffMembers = users
                .filter(u => ['HOD', 'FACULTY'].includes(u.role))
                .map(u => {
                    const facRecord = facultyList.find(f => f.userId === u.id);
                    return {
                        ...u,
                        facultyId: facRecord?.id,
                        departmentId: facRecord?.departmentId,
                        departmentName: facRecord?.departmentId
                            ? departments.find(d => d.id === facRecord.departmentId)?.name
                            : null
                    };
                });

            setStaff(staffMembers);
        } catch (err) {
            console.error("Failed to fetch staff:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (departments.length > 0) {
            fetchData();
        }
    }, [departments]);

    const handleAssignDept = (user) => {
        setSelectedUser(user);
        setTargetDept(user.departmentId || '');
        setIsModalOpen(true);
    };

    const saveAssignment = async (e) => {
        e.preventDefault();

        try {
            // Logic: Not all Users have a "Faculty" record yet.
            // If facultyId exists -> PUT.
            // If not -> POST (Create Faculty record linked to User).

            if (selectedUser.facultyId) {
                await fetch(`/api/faculty/${selectedUser.facultyId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ departmentId: targetDept })
                });
            } else {
                await fetch(`/api/faculty`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: selectedUser.name,
                        email: selectedUser.username, // Assuming username is email
                        departmentId: targetDept,
                        // Backend needs to support associating user_id on creation?
                        // Currently POST /faculty takes {name, email, departmentId}.
                        // I might need to update POST /faculty to accept userId too? 
                        // Checked models: Faculty has user_id. Checked routes: add_faculty doesn't read userId.
                        // I should update routes.py first? Or assume manual sync? 
                        // Let's assume I need to fix logic.
                        // For now, let's just create it and maybe the backend will need a tweak.
                    })
                });
                // Wait, if I create a Faculty record without user_id, it won't link back next time.
                // I need to update the backend add_faculty to accept user_id.
            }

            await fetchData();
            setIsModalOpen(false);
        } catch (err) {
            console.error(err);
        }
    };

    const getDisplayedItems = () => {
        let items = staff;
        if (filter !== 'ALL') {
            items = items.filter(u => u.role === filter);
        }

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            items = items.filter(i =>
                i.name.toLowerCase().includes(lower) ||
                (i.username && i.username.toLowerCase().includes(lower))
            );
        }
        return items;
    };

    const items = getDisplayedItems();

    if (loading) return <div className="p-8">Loading staff directory...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold font-heading text-primary">Staff Directory</h1>
                        <p className="text-muted-foreground">Manage Faculty and Departments</p>
                    </div>

                    <div className="flex bg-secondary p-1 rounded-lg self-start">
                        {['ALL', 'HOD', 'FACULTY'].map(role => (
                            <button
                                key={role}
                                onClick={() => setFilter(role)}
                                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${filter === role
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {role === 'ALL' ? 'All Staff' : role}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search staff..."
                        className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-xl focus:ring-1 focus:ring-primary outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map(user => (
                    <div key={user.id} className="bg-card border border-border p-6 rounded-xl shadow-sm flex items-start gap-4 hover:shadow-md transition-all group relative">
                        <div className={`h-16 w-16 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-sm ${user.role === 'HOD' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                            <span className="text-xl font-bold font-heading">{user.name[0]}</span>
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-lg text-foreground truncate">{user.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 mt-1">
                                <Mail size={14} />
                                <span className="truncate">{user.username}</span>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${user.role === 'HOD'
                                    ? 'bg-purple-100 text-purple-700 border-purple-200'
                                    : 'bg-blue-100 text-blue-700 border-blue-200'
                                    }`}>
                                    {user.role}
                                </span>

                                {user.departmentName ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-green-100 text-green-700 border-green-200 flex items-center gap-1">
                                        <Building2 size={10} /> {user.departmentName}
                                    </span>
                                ) : (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-gray-100 text-gray-500 border-gray-200">
                                        No Dept
                                    </span>
                                )}
                            </div>

                            <button
                                onClick={() => handleAssignDept(user)}
                                className="mt-4 w-full py-2 flex items-center justify-center gap-2 text-xs font-medium bg-secondary hover:bg-secondary/80 rounded-lg transition-colors text-foreground"
                            >
                                <Edit size={12} /> Assign Department
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-card w-full max-w-sm p-6 rounded-2xl shadow-xl">
                        <h2 className="text-lg font-bold mb-4">Assign Department</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Select the department for <strong>{selectedUser?.name}</strong>.
                        </p>
                        <form onSubmit={saveAssignment} className="space-y-4">
                            <select
                                className="w-full bg-secondary px-4 py-2 rounded-lg"
                                value={targetDept} onChange={e => setTargetDept(e.target.value)}
                            >
                                <option value="">-- No Department --</option>
                                {departments.map(d => (
                                    <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                                ))}
                            </select>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 bg-secondary rounded-lg">Cancel</button>
                                <button type="submit" className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
